import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Service } from 'webapp-types';
import { replaceAll } from 'webapp/utils/common';
import { getFormattedTime } from 'webapp/utils/number';

import FormDatetimePickerComponent from 'webapp/components/form/datetime-picker';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { Metric } from 'webapp/models-custom';
import MetricSetup from 'webapp/models-custom/metric-setup';
import { mapSourceToDataSource } from 'webapp/models/helpers/react-model-mapper';

interface Args {
	model: Metric;
	onClose: () => void;
}

export default class MetricPushIndexComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('data-manager') declare dataManagerService: Service.DataManager;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked submitInProgress = false;

	@tracked metricSetup: MetricSetup;

	@tracked value = '1';
	@tracked selectedTimestamp = moment.tz(this.timezone).startOf('day').unix();

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.metricSetup = new MetricSetup(
			Object.assign(
				{ allowAllUnits: false, allowAllDimensionValues: false },
				this.args.model.metricData!,
			),
		);
	}

	get timestampFormat(): string {
		return this.accountService.accountDatetimeFormat;
	}

	get source() {
		return this.args.model.source!;
	}

	get timezone(): string {
		return this.source.timezone;
	}

	get timezoneLabel(): string {
		return this.constantsService.timezones.find((t) => t.value === this.timezone)?.label || '';
	}

	get selectedTimestampFormatted(): string {
		return getFormattedTime(this.selectedTimestamp, this.timezone, this.timestampFormat);
	}

	get selectedUnit(): string {
		return this.metricSetup.unitName;
	}

	get disableSubmit(): boolean {
		return (
			(this.metricSetup.mustSelectDimension && !this.metricSetup.isDimensionSelected) ||
			(this.metricSetup.allowBasicDimensionValueFilters &&
				!this.metricSetup.firstAttributeNameFromBasicFilters) ||
			!(this.value || this.value === '0')
		);
	}

	get showLearnMore(): boolean {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	@action
	changeDimensions(value: string[]): void {
		this.metricSetup.dimensions = value;
		this.changeAttribute('');
	}

	@action
	changeAttribute(value: string | null): void {
		this.metricSetup.setFiltersFromAttributeName(value);
	}

	@action
	changeUnit(value: string | null): void {
		// we allow pushing unit for metric that has no units yet
		if (!this.metricSetup.allowUnitNameFilter) {
			// direct prop update is required because otherwise metricSetup does not set unitName
			// let us hope user actually does click push
			this.args.model.directPropUpdate({ hasUnits: true });
		}
		this.metricSetup.unitName = value;
	}

	@action
	changeTimestampFormatted(value: string): void {
		if (moment(value, this.timestampFormat).isValid()) {
			this.selectedTimestamp = +moment
				.tz(value, this.timestampFormat, this.timezone)
				.format('X');
		}
	}

	@action
	changeTimezone(): void {
		const source = this.source;
		if (source) {
			const dataSource = mapSourceToDataSource(source, this.currentUserService.numericId);
			this.dataManagerService.changeSourceTimezone(dataSource);
		}
	}

	@action
	save(): void {
		this.submitInProgress = true;

		const key = this.args.model.key,
			metricSetup = this.metricSetup,
			payload = {
				date: moment.unix(this.selectedTimestamp).tz(this.timezone).toISOString(true),
				filters: metricSetup.filtersData,
				value: +replaceAll(this.value, ',', ''),
			};

		this.requestService
			.api('POST', `/metrics/${encodeURIComponent(key)}/data`, payload)
			.then(() => {
				this.infolayerService.ok('Value stored!');

				if (metricSetup.firstAttributeNameFromBasicFilters) {
					// reset dimension values query in case we added a new one...
					this.mmMetricsService.cleanupDimensionsCacheForMetricData(
						this.metricSetup.metricData!,
					);
				}
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="row">
			<p>
				You can use this interface to manually store values to your custom metrics.
				<br />
				To automate storing and creating a custom integration you should use our API.
				{{#if this.showLearnMore}}
					<br />
					<a href="https://developers.databox.com" target="_blank">
						Developers documentation
					</a>
				{{/if}}
			</p>
		</div>

		<div class="row parts-2">
			<div class="part">
				<FormInputComponent
					name="metric_name"
					placeholder="Metric name"
					disabled={{true}}
					@value={{@model.name}}
				/>
			</div>
			<div class="part">
				<FormInputComponent
					name="metric_id"
					placeholder="Metric key identifier"
					disabled={{true}}
					@value={{@model.metricId}}
				/>
			</div>
		</div>

		{{#if this.metricSetup.allowDimensions}}
			<div class="row parts-2">
				<div class="part">
					<FormSelectDimensionsComponent
						@metricSetup={{this.metricSetup}}
						@onChange={{this.changeDimensions}}
						@placeholder="Select dimension"
					/>
				</div>
				<div class="part">
					<FormSelectAttributesComponent
						@selected={{this.metricSetup.firstAttributeNameFromBasicFilters}}
						@metricSetupForQuery={{this.metricSetup}}
						@onChange={{this.changeAttribute}}
						@isDisabled={{this.submitInProgress}}
					/>
				</div>
			</div>
		{{/if}}

		<div class="row">
			<FormDatetimePickerComponent
				@value={{this.selectedTimestampFormatted}}
				@onChange={{this.changeTimestampFormatted}}
				@isDisabled={{this.submitInProgress}}
			/>
			<p>
				General data source timezone is set to:
				<strong>{{this.timezoneLabel}}</strong>
				<a {{on "click" this.changeTimezone}}>Change</a>
			</p>
		</div>

		<div class="row parts-2">
			<div class="part">
				<FormInputComponent
					name="value"
					@type="number"
					placeholder="Metric value"
					disabled={{this.submitInProgress}}
					@value={{this.value}}
				/>
			</div>
			<div class="part">
				<FormSelectUnitsComponent
					@selected={{this.selectedUnit}}
					@metricSetupForQuery={{this.metricSetup}}
					@allowAllUnits={{false}}
					@onChange={{this.changeUnit}}
					@isDisabled={{this.submitInProgress}}
				/>
			</div>
			<p>
				A metric value must be purely numeric. For currency values, you can attach a unit to
				it (optional).
				{{#if this.showLearnMore}}
					<a href="https://developers.databox.com/units/" target="_blank">Learn more</a>
				{{/if}}
			</p>
		</div>

		<div class="controls2">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Close"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{@onClose}}
				/>
				<SubmitButtonComponent
					@text="Store value"
					@submitInProgress={{this.submitInProgress}}
					@isDisabled={{this.disableSubmit}}
					@onTrigger={{this.save}}
				/>
			</div>
		</div>
	</template>
}
